import React, { useState, useEffect } from 'react';
import {
  subscribe,
  getWebVitalsData,
  getApplicationMetrics,
  getResourceMetrics,
  getAPIMetrics,
  getUserExperienceMetrics,
  getPerformanceBudgets,
  getPerformanceAlerts,
  getMonitoringComparison,
  getPerformanceTrends,
  getPerformanceSummary,
  getRealTimeMetrics,
  analyzePerformanceIssues,
  getPerformanceGrade,
  generatePerformanceReport,
  refreshData,
  getPerformanceThresholds
} from '../../../services/bread/performanceReportsService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const PerformanceReports = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7d');
  const [realTimeData, setRealTimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('webVitals');

  useEffect(() => {
    const unsubscribe = subscribe(setPerformanceData);
    setLoading(false);
    
    // Log access
    addAuditLog({ user: 'feros.khan@krotrust.org', action: 'page_access', target: 'performance_reports', status: 'success' });
    
    // Setup real-time data updates
    const realTimeInterval = setInterval(() => {
      setRealTimeData(getRealTimeMetrics());
    }, 10000); // Update every 10 seconds

    // Initial real-time data
    setRealTimeData(getRealTimeMetrics());

    return () => {
      unsubscribe();
      clearInterval(realTimeInterval);
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData(30);
    addAuditLog({ user: 'feros.khan@krotrust.org', action: 'data_refresh', target: 'performance_data', status: 'success' });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleGenerateReport = () => {
    const report = generatePerformanceReport({
      dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      dateTo: new Date().toISOString().slice(0, 10)
    });
    
    // Create and download report
    const dataStr = JSON.stringify(report, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `performance_report_${new Date().toISOString().slice(0, 10)}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    addAuditLog({ user: 'feros.khan@krotrust.org', action: 'report_export', target: 'performance_report', status: 'success' });
  };

  if (loading || !performanceData) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading performance data...</span>
          </div>
          <p className="mt-2">Loading performance reports...</p>
        </div>
      </div>
    );
  }

  const summary = getPerformanceSummary();
  const webVitals = getWebVitalsData();
  const appMetrics = getApplicationMetrics();
  const resourceMetrics = getResourceMetrics();
  const apiMetrics = getAPIMetrics();
  const userExp = getUserExperienceMetrics();
  const budgets = getPerformanceBudgets();
  const alerts = getPerformanceAlerts();
  const monitoring = getMonitoringComparison();
  const trends = getPerformanceTrends();
  const issues = analyzePerformanceIssues();
  const thresholds = getPerformanceThresholds();

  const OverviewTab = () => {
    const performanceGrade = getPerformanceGrade(summary?.webVitalsScore || 0);
    
    return (
      <div className="row">
        {/* Real-time Performance Dashboard */}
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-tachometer-alt me-2"></i>Real-time Performance Monitor
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-success">{realTimeData?.currentLoad || 0}%</div>
                    <small className="text-muted">Current Load</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-info">{realTimeData?.responseTime || 0}ms</div>
                    <small className="text-muted">Response Time</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-warning">{realTimeData?.activeConnections || 0}</div>
                    <small className="text-muted">Active Connections</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-primary">{realTimeData?.requestsPerSecond || 0}</div>
                    <small className="text-muted">Requests/sec</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-danger">{realTimeData?.cpuUsage || 0}%</div>
                    <small className="text-muted">CPU Usage</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-secondary">{realTimeData?.memoryUsage || 0}%</div>
                    <small className="text-muted">Memory Usage</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="col-md-8 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>Performance Summary (Last 7 Days)
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h2" style={{ color: performanceGrade.color }}>
                      {performanceGrade.grade}
                    </div>
                    <div className="h5">{summary?.webVitalsScore || '0'}/100</div>
                    <small className="text-muted">Web Vitals Score</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-info">{summary?.responseTime || '0'}ms</div>
                    <small className="text-muted">Avg Response Time</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-success">{summary?.availability || '0'}%</div>
                    <small className="text-muted">Availability</small>
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h4 text-warning">{summary?.pageLoadTime || '0'}ms</div>
                    <small className="text-muted">Page Load Time</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h4 text-danger">{summary?.errorRate || '0'}%</div>
                    <small className="text-muted">Error Rate</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded">
                    <div className="h4 text-primary">{summary?.totalRequests?.toLocaleString() || '0'}</div>
                    <small className="text-muted">Total Requests</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Grade */}
        <div className="col-md-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-medal me-2"></i>Performance Grade
              </h5>
            </div>
            <div className="card-body text-center">
              <div className="display-1 mb-3" style={{ color: performanceGrade.color }}>
                {performanceGrade.grade}
              </div>
              <h4>{performanceGrade.description}</h4>
              <p className="text-muted">Based on industry standards and Core Web Vitals</p>
              <div className="mt-3">
                <div className="progress" style={{ height: '10px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      width: `${summary?.webVitalsScore || 0}%`,
                      backgroundColor: performanceGrade.color
                    }}
                  ></div>
                </div>
                <small className="text-muted">{summary?.webVitalsScore || 0}/100 Points</small>
              </div>
            </div>
          </div>
        </div>

        {/* Active Alerts */}
        {alerts.filter(alert => alert.status === 'active').length > 0 && (
          <div className="col-12 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i>Active Performance Alerts
                </h5>
              </div>
              <div className="card-body">
                {alerts.filter(alert => alert.status === 'active').map((alert, index) => (
                  <div key={index} className={`alert alert-${alert.severity === 'critical' ? 'danger' : 'warning'} mb-2`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{alert.metric}</strong>: {alert.description}
                        <br />
                        <small>Current: {alert.current} | Threshold: {alert.threshold}</small>
                      </div>
                      <span className={`badge bg-${alert.severity === 'critical' ? 'danger' : 'warning'}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Performance Issues */}
        {issues.length > 0 && (
          <div className="col-12 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-bug me-2"></i>Performance Issues & Recommendations
                </h5>
              </div>
              <div className="card-body">
                {issues.map((issue, index) => (
                  <div key={index} className={`alert alert-${issue.type === 'critical' ? 'danger' : 'warning'} mb-3`}>
                    <h6 className="alert-heading">{issue.metric}</h6>
                    <p className="mb-1"><strong>Issue:</strong> {issue.impact}</p>
                    <p className="mb-1"><strong>Current:</strong> {issue.value} | <strong>Threshold:</strong> {issue.threshold}</p>
                    <hr />
                    <p className="mb-0"><strong>Recommendation:</strong> {issue.recommendation}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const WebVitalsTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-heart me-2"></i>Core Web Vitals Performance
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>LCP (ms)</th>
                    <th>FID (ms)</th>
                    <th>CLS</th>
                    <th>FCP (ms)</th>
                    <th>TTFB (ms)</th>
                    <th>Score</th>
                    <th>Sample Size</th>
                  </tr>
                </thead>
                <tbody>
                  {webVitals.slice(-14).map((day, index) => {
                    const grade = getPerformanceGrade(day.score);
                    return (
                      <tr key={index}>
                        <td>{new Date(day.date).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge ${
                            day.lcp <= thresholds.coreWebVitals.lcp.good ? 'bg-success' : 
                            day.lcp <= thresholds.coreWebVitals.lcp.needs_improvement ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {day.lcp}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            day.fid <= thresholds.coreWebVitals.fid.good ? 'bg-success' : 
                            day.fid <= thresholds.coreWebVitals.fid.needs_improvement ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {day.fid}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            day.cls <= thresholds.coreWebVitals.cls.good ? 'bg-success' : 
                            day.cls <= thresholds.coreWebVitals.cls.needs_improvement ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {day.cls.toFixed(3)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            day.fcp <= thresholds.coreWebVitals.fcp.good ? 'bg-success' : 
                            day.fcp <= thresholds.coreWebVitals.fcp.needs_improvement ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {day.fcp}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            day.ttfb <= thresholds.coreWebVitals.ttfb.good ? 'bg-success' : 
                            day.ttfb <= thresholds.coreWebVitals.ttfb.needs_improvement ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {day.ttfb}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ backgroundColor: grade.color, color: 'white' }}>
                            {day.score}
                          </span>
                        </td>
                        <td>{day.sampleSize.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ApplicationTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-server me-2"></i>Application Performance Metrics
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Avg Response Time</th>
                    <th>P95 Response Time</th>
                    <th>P99 Response Time</th>
                    <th>Throughput (RPM)</th>
                    <th>Error Rate</th>
                    <th>Availability</th>
                    <th>Total Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {appMetrics.slice(-14).map((day, index) => (
                    <tr key={index}>
                      <td>{new Date(day.date).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${
                          day.responseTime <= thresholds.applicationMetrics.responseTime.good ? 'bg-success' : 
                          day.responseTime <= thresholds.applicationMetrics.responseTime.needs_improvement ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {day.responseTime}ms
                        </span>
                      </td>
                      <td>{day.p95ResponseTime}ms</td>
                      <td>{day.p99ResponseTime}ms</td>
                      <td>{day.throughput.toLocaleString()}</td>
                      <td>
                        <span className={`badge ${
                          day.errorRate <= thresholds.applicationMetrics.errorRate.good ? 'bg-success' : 
                          day.errorRate <= thresholds.applicationMetrics.errorRate.needs_improvement ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {(day.errorRate * 100).toFixed(2)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          day.availability >= thresholds.applicationMetrics.availability.good ? 'bg-success' : 
                          day.availability >= thresholds.applicationMetrics.availability.needs_improvement ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {(day.availability * 100).toFixed(2)}%
                        </span>
                      </td>
                      <td>{day.totalRequests.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const APIPerformanceTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-plug me-2"></i>API Endpoint Performance
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Endpoint</th>
                    <th>Category</th>
                    <th>Avg Response Time</th>
                    <th>P95 Response Time</th>
                    <th>P99 Response Time</th>
                    <th>Requests/Min</th>
                    <th>Error Rate</th>
                    <th>Availability</th>
                    <th>Total Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {apiMetrics.map((api, index) => (
                    <tr key={index}>
                      <td><code>{api.endpoint}</code></td>
                      <td><span className="badge bg-secondary">{api.category}</span></td>
                      <td>
                        <span className={`badge ${
                          api.avgResponseTime <= 200 ? 'bg-success' : 
                          api.avgResponseTime <= 500 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {api.avgResponseTime}ms
                        </span>
                      </td>
                      <td>{api.p95ResponseTime}ms</td>
                      <td>{api.p99ResponseTime}ms</td>
                      <td>{api.requestsPerMinute}</td>
                      <td>
                        <span className={`badge ${
                          api.errorRate <= 0.01 ? 'bg-success' : 
                          api.errorRate <= 0.05 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {(api.errorRate * 100).toFixed(2)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          api.availability >= 0.999 ? 'bg-success' : 
                          api.availability >= 0.99 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {(api.availability * 100).toFixed(2)}%
                        </span>
                      </td>
                      <td>{api.totalRequests.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BudgetsTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-bullseye me-2"></i>Performance Budgets
            </h5>
          </div>
          <div className="card-body">
            {budgets.map((budget, index) => (
              <div key={index} className="mb-4 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{budget.metric}</h6>
                  <span className={`badge ${
                    budget.status === 'within_budget' ? 'bg-success' : 
                    budget.status === 'at_risk' ? 'bg-warning' : 'bg-danger'
                  }`}>
                    {budget.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="row">
                  <div className="col-md-8">
                    <div className="progress mb-2" style={{ height: '20px' }}>
                      <div 
                        className={`progress-bar ${
                          budget.status === 'within_budget' ? 'bg-success' : 
                          budget.status === 'at_risk' ? 'bg-warning' : 'bg-danger'
                        }`}
                        style={{ width: `${Math.min((budget.current / budget.budget) * 100, 100)}%` }}
                      >
                        {budget.current} / {budget.budget}
                      </div>
                    </div>
                    <small className="text-muted">
                      Trend: <span className={`fw-bold ${
                        budget.trend === 'improving' ? 'text-success' : 
                        budget.trend === 'stable' ? 'text-warning' : 'text-danger'
                      }`}>
                        {budget.trend}
                      </span> | Impact: <span className="fw-bold">{budget.impact}</span>
                    </small>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className="h5 mb-0">{budget.current}</div>
                    <small className="text-muted">Current / Budget: {budget.budget}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const MonitoringTab = () => (
    <div className="row">
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-robot me-2"></i>Synthetic Monitoring
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-6">
                <div className="text-center">
                  <div className="h3 text-success">{(monitoring.synthetic?.availability * 100).toFixed(2)}%</div>
                  <small className="text-muted">Availability</small>
                </div>
              </div>
              <div className="col-6">
                <div className="text-center">
                  <div className="h3 text-info">{monitoring.synthetic?.responseTime}ms</div>
                  <small className="text-muted">Response Time</small>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <strong>Monitoring Locations:</strong>
              <div className="mt-1">
                {monitoring.synthetic?.locations?.map((location, index) => (
                  <span key={index} className="badge bg-secondary me-1">{location}</span>
                ))}
              </div>
            </div>
            <div className="mb-2">
              <strong>Frequency:</strong> <span className="text-muted">{monitoring.synthetic?.frequency}</span>
            </div>
            <div>
              <strong>Coverage:</strong> <span className="text-muted">{monitoring.synthetic?.coverage}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>Real User Monitoring
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-6">
                <div className="text-center">
                  <div className="h3 text-warning">{(monitoring.realUser?.availability * 100).toFixed(2)}%</div>
                  <small className="text-muted">Availability</small>
                </div>
              </div>
              <div className="col-6">
                <div className="text-center">
                  <div className="h3 text-primary">{monitoring.realUser?.responseTime}ms</div>
                  <small className="text-muted">Response Time</small>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <strong>User Sessions:</strong> <span className="text-muted">{monitoring.realUser?.userSessions?.toLocaleString()}</span>
            </div>
            <div className="mb-3">
              <strong>Device Types:</strong>
              <div className="mt-1">
                {monitoring.realUser?.deviceTypes?.map((device, index) => (
                  <div key={index} className="small text-muted">{device}</div>
                ))}
              </div>
            </div>
            <div>
              <strong>Browsers:</strong>
              <div className="mt-1">
                {monitoring.realUser?.browsers?.map((browser, index) => (
                  <div key={index} className="small text-muted">{browser}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Performance Reports</h2>
              <p className="text-muted">Industry-standard application performance monitoring and analytics</p>
            </div>
            <div>
              <button 
                className="btn btn-success me-2"
                onClick={handleGenerateReport}
              >
                <i className="fas fa-download me-2"></i>Export Report
              </button>
              <button 
                className="btn btn-outline-primary"
                onClick={handleRefreshData}
                disabled={loading}
              >
                <i className="fas fa-sync-alt me-2"></i>Refresh Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills">
            {[
              { id: 'overview', label: 'Overview', icon: 'fas fa-tachometer-alt' },
              { id: 'webvitals', label: 'Web Vitals', icon: 'fas fa-heart' },
              { id: 'application', label: 'Application', icon: 'fas fa-server' },
              { id: 'api', label: 'API Performance', icon: 'fas fa-plug' },
              { id: 'budgets', label: 'Performance Budgets', icon: 'fas fa-bullseye' },
              { id: 'monitoring', label: 'Monitoring', icon: 'fas fa-eye' }
            ].map(tab => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>{tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'webvitals' && <WebVitalsTab />}
        {activeTab === 'application' && <ApplicationTab />}
        {activeTab === 'api' && <APIPerformanceTab />}
        {activeTab === 'budgets' && <BudgetsTab />}
        {activeTab === 'monitoring' && <MonitoringTab />}
      </div>
    </div>
  );
};

export default PerformanceReports;